(() => {
    class ManageCampaignTemplateController {

        canRevertToDraft = false;
        /**
         * @type {(campaigns: ActiveCampaign[]) => void}
         */
        boundOnActiveCampaignsRefreshed;
        /**
         * @type {CampaignTemplate}
         */
        campaignTemplate;
        navigationDisabled;
        navigationDisabledMessage;

        constructor(
            $rootScope,
            $scope,
            $state,
            PermissionService,
            CampaignDesignerService,
            ErrorService,
            notify,
            ActiveCampaignsService
        ) {
            this.$rootScope = $rootScope;
            this.$scope = $scope;
            this.$state = $state;
            this.PermissionService = PermissionService;
            this.CampaignDesignerService = CampaignDesignerService;
            this.ErrorService = ErrorService;
            this.notify = notify;
            this.campaignTemplateGuid = $state.params.CampaignTemplateGuid;
            this.brandName = "";
            this.ActiveCampaignsService = ActiveCampaignsService;

            this._setCampaignTemplate({});
            this._setUserContext(this.PermissionService.userContext);
            this.master = this;
            this.campaignTemplateLoaded = false;
            this.statesWhichCanBeRevertedToDraft = new Set([
                window.Dashboard.Models.Lookups.CampaignTemplateStates.Published,
                window.Dashboard.Models.Lookups.CampaignTemplateStates.Archived
            ]);
            this.navigationDisabled = false;
            this.navigationDisabledMessage = "";
        }

        $onInit() {
            this.boundOnActiveCampaignsRefreshed =
                campaigns => this.handleIsTemplateActiveResult(campaigns);
            this.brandName = this.userContext.BrandName;
            this.checkUserAccessToCampaignDesigner();
            this.loadCampaignTemplate();
            this.$rootScope.$on("disable-wizard-navigation", (src, state) => {
                this.navigationDisabled = state.disabled;
                this.navigationDisabledMessage = state.message;
            });
        }

        $onDestroy() {
            this.ActiveCampaignsService.stopAllWatchers();
        }

        get revertToDraftTitle() {
            return this.canRevertToDraft
                ? "Revert this template to draft state"
                : "This template is in active use and may not be reverted to draft state at this time."
        }

        get campaignTemplateIsInDraftState() {
            if (!this.campaignTemplate) {
                return true; // at least this hides the button
            }
            return this.campaignTemplate.StateId ===
                window.Dashboard.Models.Lookups.CampaignTemplateStates.Draft;
        }

        get campaignTemplateStatus() {
            if (!this.campaignTemplate) {
                return "error";
            }
            const
                states = window.Dashboard.Models.Lookups.CampaignTemplateStates,
                lookup = Object.keys(states).reduce((acc, cur) => {
                    acc[`${states[cur]}`] = cur;
                    return acc;
                }, {}),
                currentId = `${this.campaignTemplate.StateId}`;
            return lookup[currentId] || "unknown";
        }

        /**
         * @param {boolean} isActive
         */
        handleIsTemplateActiveResult(isActive) {
            if (isActive === null || isActive === undefined) {
                return this.canRevertToDraft = false;
            }
            if (!this.campaignTemplate) {
                return this.canRevertToDraft = false;
            }
            if (!this.statesWhichCanBeRevertedToDraft.has(this.campaignTemplate.StateId)) {
                return this.canRevertToDraft = false;
            }
            this.canRevertToDraft = !isActive;
        }

        _setCampaignTemplate(campaignTemplate) {
            // wizard step components expect to find this on $scope
            this.campaignTemplate = this.$scope.campaignTemplate = campaignTemplate;
            if (this.userContext) {
                campaignTemplate.BrandId = this.userContext.BrandId;
            }
            if (this.campaignTemplate.Id) {
                this._startActiveCampaignTemplateWatcher(
                    campaignTemplate.Id
                );
            } else {
                this._resetCampaignWatcher();
            }
        }

        _startActiveCampaignTemplateWatcher(
            campaignTemplateId
        ) {
            this._startCampaignWatcher(
                this.ActiveCampaignsService.watchTemplate(
                    campaignTemplateId
                )
            );
        }

        _startCampaignWatcher(
            activeCampaignWatcher
        ) {
            this._resetCampaignWatcher();
            this.campaignWatcher = activeCampaignWatcher;
            this.campaignWatcher.subscribe(this.boundOnActiveCampaignsRefreshed);
        }

        _resetCampaignWatcher() {
            if (this.campaignWatcher) {
                this.campaignWatcher.stop();
            }
            this.campaignWatcher = undefined;
        }

        _setUserContext(userContext) {
            // wizard step components expect to find this on $scope
            this.userContext = this.$scope.userContext = userContext;
        }

        loadCampaignTemplate() {
            if (!this.campaignTemplateGuid) {
                const campaignTemplate = new Dashboard.Models.CampaignTemplate({
                    TemplateGuid: window.Dashboard.Constants.EmptyGuid
                });
                this._setCampaignTemplate(campaignTemplate);
                this.campaignTemplateLoaded = true;
                return;
            }

            this.$scope.$root.isLoading = true;
            this.CampaignDesignerService.getCampaignTemplate(
                this.campaignTemplateGuid
            ).then(result => {
                this._setCampaignTemplate(
                    new Dashboard.Models.CampaignTemplate()
                        .InitialiseCampaignTemplate(result.data)
                );
                this.campaignTemplateLoaded = true;
            }).catch(err => {
                this.ErrorService.showError(
                    "An error occurred while loading the campaign template",
                    err
                );
            }).finally(() => {
                this.$scope.$root.isLoading = false;
            });
        }

        checkUserAccessToCampaignDesigner() {
            if (!this.PermissionService.doesUserHavePermissionForContext(
                "CampaignDesignerAccess",
                this.userContext
            )) {
                this.PermissionService.navigateToAccessDenied();
            }
        }

        getHeaderClass(headerItem) {
            const currentState = Dashboard.helpers.stateHelper.getChildState(
                this.$state.current.name
            );
            const currentItemOrder = Dashboard.Models.Lookups.CampaignTemplateManageOrder[currentState];
            const headerItemOrder = Dashboard.Models.Lookups.CampaignTemplateManageOrder[headerItem];
            const isDone = currentItemOrder > headerItemOrder;
            if (headerItem === currentState) {
                return "current";
            }
            if (headerItem === "stores" &&
                this.campaignTemplate.CampaignLevelId === Dashboard.Models.Lookups.CampaignLevels.Brand) {
                return isDone ? "disabled done" : "disabled";
            }
            if (headerItem === "comms" &&
                !this.campaignTemplate.HasCommunication()) {
                return isDone ? "disabled done" : "disabled";
            }
            if (isDone) {
                return "done";
            }
            return "disabled";
        }

        navigateToWizardStep(step) {
            const currentState = Dashboard.helpers.stateHelper.getChildState(
                this.$state.current.name
            );
            if (step === currentState || !this.campaignTemplate.TemplateGuid) {
                return;
            }
            if (this.navigationDisabled) {
                const message = this.navigationDisabledMessage || "Navigation has been disabled";
                window.alert(message);
                return;
            }

            // If this is a brand level campaign then don't allow navigation to the store associations page
            if (step === "stores" &&
                this.campaignTemplate.CampaignLevelId === Dashboard.Models.Lookups.CampaignLevels.Brand) {
                return;
            }

            if (step === "manage") {
                step = Dashboard.helpers.stateHelper.getParentState(
                    this.$state.current.name
                );
            } else {
                if (currentState === "manage") {
                    step = Dashboard.helpers.stateHelper.getParentState(
                        this.$state.current.name
                    ) + `.manage.${step}`;
                } else {
                    step = Dashboard.helpers.stateHelper.getParentState(
                        this.$state.current.name) + `.${step}`
                }
            }
            this.$state.go(
                step, {
                    brandId: this.userContext.BrandId,
                    CampaignTemplateGuid: this.campaignTemplate.TemplateGuid
                });
        };

        canArchiveCampaignTemplate() {
            return this.campaignTemplate &&
                this.campaignTemplate.StateId !== Dashboard.Models.Lookups.CampaignTemplateStates.Archived;
        }

        archiveCampaignTemplate() {
            this.CampaignDesignerService.archiveCampaignTemplate(this.campaignTemplateGuid)
                .then(() => {
                    this.notify.success("Campaign template has been archived");
                    this.loadCampaignTemplate();
                })
                .catch((error) => {
                    this.ErrorService.showError(
                        "Failed to archive campaign",
                        error
                    );
                });
        };

        unArchiveCampaignTemplate() {
            this.CampaignDesignerService.unArchiveCampaignTemplate(
                this.campaignTemplateGuid
            ).then(() => {
                this.notify.success("Campaign template has been un-archived");
                this.loadCampaignTemplate();
            }).catch((error) => {
                this.ErrorService.showError(
                    "Failed to un-archive campaign",
                    error
                );
            });
        }

        revertCampaignTemplateToDraft() {
            return this.CampaignDesignerService.revertCampaignTemplateToDraft(
                this.campaignTemplate.Id
            ).then(res => {
                if (res.data) {
                    this.notify.success("Campaign template has been reverted to draft state");
                    this.loadCampaignTemplate();
                } else {
                    window.alert("Unable to revert campaign to draft state.");
                }
            })
        }
    }

    angular
        .module("dashboard")
        .component("manageCampaignTemplate", {
            templateUrl: "components/campaigndesigner/managecampaigntemplates/managecampaigntemplate.50afa197.html",
            bindings: { viewOrder: "@" },
            controller: [
                "$rootScope",
                "$scope",
                "$state",
                "PermissionService",
                "CampaignDesignerService",
                "ErrorService",
                "notify",
                "ActiveCampaignsService",
                ManageCampaignTemplateController
            ]
        });
})();
